import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { nanoid as nanoidGenerator } from 'nanoid';
import {
	Box,
	Typography,
	Card,
	CardContent,
	Button,
	Avatar,
	Chip,
	IconButton,
	Collapse,
	Alert,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogContentText,
	DialogActions,
	Tooltip,
	CircularProgress,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import GroupAddIcon from '@mui/icons-material/GroupAdd';

import { ShowAlertState } from 'features/common/types';
import { RootState } from 'store';
import { IPassenger, IRelatedPax, NetsuitePlaces } from 'features/salesOrder/types';
import { getNetsuitePlaces, removePax, getRelatedPaxes } from 'features/salesOrder/services';
import { PassengerForm } from './PassengerForm';
import { IPassengerForm } from 'features/salesOrder/types';
import { getPassengerId } from 'features/salesOrder/services';
import SearchPassenger from './SearchPassenger';
import { getNsCountryId } from './helpers';

interface Props {
	passengers: IPassenger[];
	onUpdatePassengers: () => void;
	onSetAlert: (values: ShowAlertState) => void;
	onNavigationAttempt?: (handler: (callback: () => void) => void) => void; // Callback para manejar intentos de navegación
}

// Función helper para limpiar imágenes base64
const cleanBase64Image = (imageString: string | null): string | null => {
	if (!imageString) return null;

	// Si la imagen tiene prefijo de data URL, removerlo
	if (imageString.startsWith('data:image/')) {
		const base64Part = imageString.split(',')[1];
		return base64Part || imageString;
	}

	// Si ya está limpia, retornarla tal como está
	return imageString;
};

// Función para determinar el estado de completitud de un pasajero
const getPassengerCompletionStatus = (passenger: any): 'complete' | 'incomplete' => {
	// Información básica requerida para ambos tipos
	const hasBasicInfo = passenger.name && passenger.lastName && passenger.countryId;

	if (passenger.isPhysicalPerson) {
		// Para personas físicas: necesitan info básica + fecha de nacimiento + al menos un documento
		const hasRequiredPersonalInfo = hasBasicInfo && passenger.birthdate;
		const hasAtLeastOneDocument = passenger.documents && passenger.documents.length > 0;
		return hasRequiredPersonalInfo && hasAtLeastOneDocument ? 'complete' : 'incomplete';
	} else {
		// Para empresas (personas jurídicas): solo necesitan info básica corporativa
		// No requieren fecha de nacimiento ni documentos de viaje
		const hasRequiredCorporateInfo = hasBasicInfo && passenger.address;
		return hasRequiredCorporateInfo ? 'complete' : 'incomplete';
	}
};

// Modal de confirmación para eliminar pasajero
const DeleteConfirmationModal = ({
	open,
	onClose,
	onConfirm,
	passengerName,
}: {
	open: boolean;
	onClose: () => void;
	onConfirm: () => void;
	passengerName: string;
}) => (
	<Dialog open={open} onClose={onClose}>
		<DialogTitle>¿Eliminar pasajero?</DialogTitle>
		<DialogContent>
			<DialogContentText>
				¿Estás seguro que quieres eliminar a <strong>{passengerName}</strong> de esta orden? Esta acción no se puede
				deshacer.
			</DialogContentText>
		</DialogContent>
		<DialogActions>
			<Button onClick={onClose} color="inherit">
				Cancelar
			</Button>
			<Button onClick={onConfirm} color="error" variant="contained">
				Eliminar
			</Button>
		</DialogActions>
	</Dialog>
);

// Componente de cards de pasajeros sugeridos/relacionados
const SuggestedPaxCards = ({ suggestions, onAdd }: { suggestions: IRelatedPax[]; onAdd: (id: string) => void }) => (
	<Box
		sx={{
			ml: 3,
			mb: 2,
			pl: 2,
			borderLeft: '3px solid',
			borderColor: 'primary.light',
		}}
	>
		<Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', fontWeight: 600 }}>
			Pasajeros relacionados sugeridos
		</Typography>
		<Box display="flex" flexWrap="wrap" gap={1}>
			{suggestions.map((pax) => (
				<Box
					key={pax.id}
					sx={{
						display: 'flex',
						alignItems: 'center',
						gap: 1.5,
						px: 2,
						py: 1,
						border: '1px solid',
						borderColor: 'divider',
						borderRadius: 1,
						bgcolor: 'background.paper',
						boxShadow: 1,
					}}
				>
					<Box>
						<Typography variant="body2" fontWeight={600} lineHeight={1.3}>
							{pax.name} {pax.lastName}
						</Typography>
						<Typography variant="caption" color="text.secondary">
							{pax.birthdate} • {pax.completeDocumentNumber}
						</Typography>
					</Box>
					<Button size="small" variant="outlined" onClick={() => onAdd(pax.id)}>
						Agregar
					</Button>
				</Box>
			))}
		</Box>
	</Box>
);

// Componente para cada card de pasajero
const PassengerCard = ({
	passenger,
	isExpanded,
	onToggleExpand,
	onRemove,
	places,
	onUpdatePassengers,
	onSetAlert,
	onFetchRelated,
	loadingRelated,
}: {
	passenger: any;
	isExpanded: boolean;
	onToggleExpand: () => void;
	onRemove: (keyId: string) => void;
	places: NetsuitePlaces | undefined;
	onUpdatePassengers: () => void;
	onSetAlert: (values: ShowAlertState) => void;
	onFetchRelated: (passengerId: string) => void;
	loadingRelated: boolean;
}) => {
	const [deleteModalOpen, setDeleteModalOpen] = useState(false);
	const status = getPassengerCompletionStatus(passenger);
	const isComplete = status === 'complete';

	const handleSavePassenger = () => {
		// Trigger el submit del formulario
		const form = document.getElementById(`passenger-form-${passenger.keyId}`) as HTMLFormElement;
		if (form) {
			form.requestSubmit();
		}
	};

	const handleDeleteConfirm = () => {
		onRemove(passenger.keyId);
		setDeleteModalOpen(false);
	};

	const handleCardClick = (e: React.MouseEvent) => {
		// No expandir si se hace click en los botones de acción
		if ((e.target as HTMLElement).closest('.action-buttons')) {
			e.stopPropagation();
			return;
		}
		onToggleExpand();
	};

	return (
		<>
			<Card
				sx={{
					mb: 2,
					transition: 'all 0.2s ease-in-out',
					'&:hover': {
						boxShadow: 3,
					},
					boxShadow: isExpanded ? 4 : 1,
				}}
			>
				<CardContent
					sx={{
						p: 2,
						'&:last-child': { pb: 2 },
						cursor: 'pointer',
					}}
					onClick={handleCardClick}
				>
					<Box>
						{/* Primera fila: Info del pasajero + botones de acción */}
						<Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
							<Box display="flex" alignItems="center" gap={2} flex={1} minWidth={0}>
								<Avatar
									sx={{
										bgcolor: passenger.isPhysicalPerson ? 'primary.main' : 'secondary.main',
										width: 40,
										height: 40,
									}}
								>
									{passenger.isPhysicalPerson ? <PersonIcon /> : <BusinessIcon />}
								</Avatar>

								<Box flex={1} minWidth={0}>
									<Box display="flex" alignItems="center" gap={1} mb={0.5}>
										<Typography
											variant="h6"
											fontWeight={600}
											sx={{
												overflow: 'hidden',
												textOverflow: 'ellipsis',
												whiteSpace: 'nowrap',
											}}
										>
											{passenger.name || 'Nuevo Pasajero'} {passenger.lastName || ''}
										</Typography>

										{/* Punto de estado */}
										<FiberManualRecordIcon
											sx={{
												color: isComplete ? 'success.main' : 'warning.main',
												fontSize: 12,
												flexShrink: 0,
											}}
										/>
									</Box>

									<Typography variant="body2" color="text.secondary">
										{passenger.ageAtEndTravel && `${passenger.ageAtEndTravel} años • `}
										{passenger.isPhysicalPerson
											? passenger.documents?.length > 0
												? `${passenger.documents.length} documento${passenger.documents.length !== 1 ? 's' : ''}`
												: 'Sin documentos registrados'
											: 'Información fiscal y corporativa'}
									</Typography>
								</Box>
							</Box>

							{/* Botones de acción - móviles */}
							<Box display="flex" alignItems="center" gap={0.5} className="action-buttons" sx={{ flexShrink: 0 }}>
								{/* Botón de buscar relacionados */}
								{passenger.keyId && (
									<Tooltip title="Buscar pasajeros relacionados" arrow>
										<IconButton
											size="small"
											onClick={(e) => {
												e.stopPropagation();
												onFetchRelated(passenger.keyId);
											}}
											disabled={loadingRelated}
											sx={{
												backgroundColor: 'primary.light',
												color: 'white',
												'&:hover': { backgroundColor: 'primary.main' },
												width: 32,
												height: 32,
											}}
										>
											{loadingRelated ? (
												<CircularProgress size={14} sx={{ color: 'white' }} />
											) : (
												<GroupAddIcon sx={{ fontSize: 16 }} />
											)}
										</IconButton>
									</Tooltip>
								)}

								{/* Botón de guardar */}
								<Tooltip title="Guardar pasajero" arrow>
									<IconButton
										size="small"
										color="success"
										onClick={(e) => {
											e.stopPropagation();
											handleSavePassenger();
										}}
										sx={{
											backgroundColor: 'success.light',
											color: 'white',
											'&:hover': {
												backgroundColor: 'success.main',
											},
											width: 32,
											height: 32,
										}}
									>
										<SaveIcon sx={{ fontSize: 16 }} />
									</IconButton>
								</Tooltip>

								{/* Botón de eliminar */}
								<Tooltip title="Eliminar pasajero" arrow>
									<IconButton
										size="small"
										color="error"
										onClick={(e) => {
											e.stopPropagation();
											setDeleteModalOpen(true);
										}}
										sx={{
											backgroundColor: 'error.light',
											color: 'white',
											'&:hover': {
												backgroundColor: 'error.main',
											},
											width: 32,
											height: 32,
										}}
									>
										<DeleteIcon sx={{ fontSize: 16 }} />
									</IconButton>
								</Tooltip>
							</Box>
						</Box>

						{/* Segunda fila: Chips del pasajero */}
						<Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
							{passenger.mainPassenger && <Chip label="Principal" size="small" color="primary" variant="outlined" />}
							{passenger.invoiceable && <Chip label="Facturable" size="small" color="secondary" variant="outlined" />}
						</Box>
					</Box>
				</CardContent>

				<Collapse in={isExpanded} timeout="auto" unmountOnExit>
					<Box sx={{ p: 2 }}>
						{places ? (
							<PassengerForm
								isInDb={Boolean(passenger.id)}
								passenger={passenger}
								onUpdatePassengers={onUpdatePassengers}
								onSetAlert={onSetAlert}
								onSetLoading={() => {
									/* No-op */
								}}
								onSaveComplete={onToggleExpand}
								places={places}
							/>
						) : (
							<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
								<Typography variant="body2" color="text.secondary">
									Cargando datos necesarios para el formulario...
								</Typography>
							</Box>
						)}
					</Box>
				</Collapse>
			</Card>

			{/* Modal de confirmación para eliminar */}
			<DeleteConfirmationModal
				open={deleteModalOpen}
				onClose={() => setDeleteModalOpen(false)}
				onConfirm={handleDeleteConfirm}
				passengerName={`${passenger.name || 'Nuevo'} ${passenger.lastName || 'Pasajero'}`}
			/>
		</>
	);
};

// Componente para crear nuevo pasajero con búsqueda integrada
const NewPassengerCreator = ({
	onCreatePassenger,
	onSelectExistingPassenger,
	isPhysicalPerson,
	onTogglePersonType,
	existingPassengers = [],
	onDuplicateAlert,
}: {
	onCreatePassenger: () => void;
	onSelectExistingPassenger: (passengerId: string) => void;
	isPhysicalPerson: boolean;
	onTogglePersonType: () => void;
	existingPassengers?: Array<{
		keyId?: string;
		name: string;
		lastName: string;
		birthdate?: string;
		passengerId?: string;
	}>;
	onDuplicateAlert?: (message: string) => void;
}) => {
	const [showSearch, setShowSearch] = useState(true);

	return (
		<Card sx={{ p: 3, mb: 3, boxShadow: 2 }}>
			<Typography variant="h6" gutterBottom>
				Agregar Nuevo Pasajero
			</Typography>

			<Box display="flex" alignItems="center" gap={2} mb={3}>
				<Typography variant="body2">Tipo de persona:</Typography>
				<Button
					variant={isPhysicalPerson ? 'contained' : 'outlined'}
					size="small"
					onClick={onTogglePersonType}
					startIcon={<PersonIcon />}
				>
					Física
				</Button>
				<Button
					variant={!isPhysicalPerson ? 'contained' : 'outlined'}
					size="small"
					onClick={onTogglePersonType}
					startIcon={<BusinessIcon />}
				>
					Jurídica
				</Button>
			</Box>

			{showSearch && (
				<Box mb={3}>
					<SearchPassenger
						onSelectPassenger={(passengerId) => {
							onSelectExistingPassenger(passengerId);
							setShowSearch(false);
						}}
						isPhysicalPerson={isPhysicalPerson}
						placeholder={`Buscar ${isPhysicalPerson ? 'persona física' : 'persona jurídica'} existente...`}
						existingPassengers={existingPassengers}
						onDuplicateError={onDuplicateAlert}
					/>
				</Box>
			)}

			<Box display="flex" gap={2} alignItems="center">
				<Button variant="contained" onClick={onCreatePassenger} startIcon={<PersonAddIcon />}>
					Crear Nuevo Pasajero
				</Button>

				<Typography variant="body2" color="text.secondary">
					o busca uno existente arriba
				</Typography>
			</Box>
		</Card>
	);
};

export const Paxes = ({ passengers, onUpdatePassengers, onSetAlert, onNavigationAttempt }: Props): JSX.Element => {
	const [places, setPlaces] = useState<NetsuitePlaces>();
	const { id: saleOrderId } = useParams<{ id: string }>();
	const { auth } = useSelector((state: RootState) => state);
	const [passengersState, setPassengersState] = useState<IPassengerForm[]>([]);
	const [expandedPassenger, setExpandedPassenger] = useState<string | null>(null);
	const [showNewPassengerCreator, setShowNewPassengerCreator] = useState(false);
	const [newPassengerType, setNewPassengerType] = useState<boolean>(true); // true = física, false = jurídica
	const [relatedPaxSuggestions, setRelatedPaxSuggestions] = useState<{
		forPassengerId: string;
		suggestions: IRelatedPax[];
	} | null>(null);
	const [loadingRelatedForId, setLoadingRelatedForId] = useState<string | null>(null);
	// Estado para el modal de cambios no guardados
	const [unsavedModal, setUnsavedModal] = useState<{
		open: boolean;
		pendingCallback: (() => void) | null;
	}>({
		open: false,
		pendingCallback: null,
	});
	const mercado = auth.country;
	const marketCountry = auth.country === 'TN' ? 'PY' : auth.country;

	// Estado para trackear si realmente se agregaron pasajeros nuevos
	const [hasAddedNewPassengers, setHasAddedNewPassengers] = useState(false);

	// Función para detectar si hay cambios no guardados
	const hasUnsavedChanges = useCallback(() => {
		// Solo retornar true si explícitamente se agregaron pasajeros nuevos
		return hasAddedNewPassengers;
	}, [hasAddedNewPassengers]);

	// Función para manejar intentos de navegación
	const handleNavigationRequest = useCallback(
		(callback: () => void) => {
			if (hasUnsavedChanges()) {
				setUnsavedModal({
					open: true,
					pendingCallback: callback,
				});
			} else {
				callback();
			}
		},
		[hasUnsavedChanges],
	);

	// Exponer la función al componente padre
	useEffect(() => {
		if (onNavigationAttempt) {
			onNavigationAttempt(handleNavigationRequest);
		}
	}, [onNavigationAttempt, handleNavigationRequest]);

	// Manejar respuestas del modal
	const handleUnsavedModalResponse = useCallback(
		(action: 'stay' | 'leave') => {
			const { pendingCallback } = unsavedModal;

			setUnsavedModal({ open: false, pendingCallback: null });

			if (action === 'leave' && pendingCallback) {
				// Salir sin guardar - ejecutar navegación y resetear flag
				setHasAddedNewPassengers(false);
				pendingCallback();
			}
			// Si action === 'stay', no hacer nada (se queda en la página)
		},
		[unsavedModal],
	);

	const handleRemovePax = useCallback(
		async (keyId: string) => {
			const newState = passengersState.filter((pax) => pax.keyId !== keyId);
			const isPaxInDb = Boolean(passengers.find((pax) => pax.keyId === keyId));

			setPassengersState(newState);

			// Cerrar el acordeón si se elimina el pasajero expandido
			if (expandedPassenger === keyId) {
				setExpandedPassenger(null);
			}

			// Verificar si después de eliminar este pasajero, quedan pasajeros nuevos sin guardar
			// Si el pasajero eliminado no estaba en la DB (era nuevo) y no quedan más pasajeros nuevos,
			// resetear el flag de cambios sin guardar
			if (!isPaxInDb) {
				const remainingNewPassengers = newState.filter((pax) => !passengers.find((dbPax) => dbPax.keyId === pax.keyId));
				if (remainingNewPassengers.length === 0) {
					setHasAddedNewPassengers(false);
				}
			}

			if (isPaxInDb) {
				try {
					await removePax({ saleOrderId, paxId: keyId });
					onUpdatePassengers();
					onSetAlert({
						show: true,
						severity: 'success',
						message: 'Pasajero removido con éxito!',
					});

					// Después de eliminar exitosamente un pasajero guardado,
					// verificar si quedan pasajeros nuevos sin guardar
					const remainingNewPassengers = newState.filter(
						(pax) => !passengers.find((dbPax) => dbPax.keyId === pax.keyId),
					);
					if (remainingNewPassengers.length === 0) {
						setHasAddedNewPassengers(false);
					}
				} catch (e) {
					console.log(e);
					onSetAlert({
						show: true,
						severity: 'error',
						message: 'Error al remover el pasajero',
					});
				}
			}
		},
		[passengersState, setPassengersState, passengers, expandedPassenger, setHasAddedNewPassengers],
	);

	const handleCreateNewPassenger = useCallback(() => {
		const countryId = String(places ? getNsCountryId(places, marketCountry) : 1);
		const isFirstPassenger = passengersState.length === 0;
		const newPassenger: IPassengerForm = {
			id: nanoidGenerator(),
			keyId: nanoidGenerator(),
			name: '',
			lastName: '',
			state: '',
			municipality: '',
			province: '',
			birthdate: '',
			isPhysicalPerson: newPassengerType,
			invoiceable: false,
			mainPassenger: isFirstPassenger,
			countryId,
			postalCode: '',
			address: '',
			syncStatus: null,
			documents: [],
			frequentFlyerNumbers: [],
		};

		setPassengersState((prev) => [...prev, newPassenger]);
		setExpandedPassenger(newPassenger.keyId);
		setShowNewPassengerCreator(false);
		setHasAddedNewPassengers(true); // Marcar que se agregó un pasajero nuevo
	}, [passengersState, places, marketCountry, newPassengerType]);

	const handleSelectExistingPassenger = useCallback(
		async (passengerId: string) => {
			try {
				// Obtener los datos completos del pasajero existente
				const { data: existingPassenger } = await getPassengerId(passengerId);

				// Cast a any para acceder a todas las propiedades que devuelve el API
				const fullPassengerData = existingPassenger as any;

				// Crear un nuevo pasajero basado en los datos existentes PERO SIN GUARDARLO AÚN
				const countryId = String(places ? getNsCountryId(places, marketCountry) : 1);
				const isFirstPassenger = passengersState.length === 0;

				const prepopulatedPassenger: IPassengerForm = {
					id: '', // Sin ID porque aún no está guardado
					keyId: fullPassengerData.keyId, // Usar el keyId existente para agregarlo a la orden
					name: fullPassengerData.name || '',
					lastName: fullPassengerData.lastName || '',
					state: fullPassengerData.state || '',
					municipality: fullPassengerData.municipality || '',
					province: fullPassengerData.province || '',
					birthdate: fullPassengerData.birthdate || '',
					isPhysicalPerson: fullPassengerData.isPhysicalPerson ?? newPassengerType,
					invoiceable: fullPassengerData.invoiceable ?? false,
					mainPassenger: isFirstPassenger,
					countryId: fullPassengerData.countryId ? String(fullPassengerData.countryId) : countryId,
					postalCode: fullPassengerData.postalCode || '',
					address: fullPassengerData.address || '',
					syncStatus: null, // Se establecerá al guardar
					documents:
						fullPassengerData.documents?.map((doc: any) => ({
							keyId: doc.keyId || nanoidGenerator(),
							documentType: doc.documentType as any,
							country: typeof doc.country === 'string' ? doc.country : doc.country.code,
							documentNumber: doc.documentNumber,
							expirationDate: doc.expirationDate,
							image: cleanBase64Image(doc.image),
						})) || [],
					frequentFlyerNumbers: fullPassengerData.frequentFlyerNumbers || [],
					phone: fullPassengerData.phone || '',
					email: fullPassengerData.email || '',
					documentVerificationNumber: fullPassengerData.documentVerificationNumber || '',
					seatPreference: fullPassengerData.seatPreference || '',
					// Mapear correctamente los campos del formulario principal
					documentType: fullPassengerData.documentType || undefined,
					documentNumber: fullPassengerData.documentNumber || '',
					taxPayerTypeId: fullPassengerData.taxPayerTypeId ? String(fullPassengerData.taxPayerTypeId) : undefined,
					taxRegimeId: fullPassengerData.taxRegimeId ? String(fullPassengerData.taxRegimeId) : undefined,
					taxResidenceTypeId: fullPassengerData.taxResidenceTypeId
						? String(fullPassengerData.taxResidenceTypeId)
						: undefined,
					fiscalResponsibilityId: fullPassengerData.fiscalResponsibilityId
						? String(fullPassengerData.fiscalResponsibilityId)
						: undefined,
					ageAtEndTravel: fullPassengerData.ageAtEndTravel || undefined,
				};

				// Agregar el pasajero prepopulado al estado para mostrar el formulario
				setPassengersState((prev) => [...prev, prepopulatedPassenger]);
				setExpandedPassenger(prepopulatedPassenger.keyId); // Expandir automáticamente
				setShowNewPassengerCreator(false);
				setHasAddedNewPassengers(true); // Marcar que se agregó un pasajero existente a la orden

				onSetAlert({
					show: true,
					severity: 'info',
					message: `Datos de ${fullPassengerData.name} ${fullPassengerData.lastName} cargados. Revisa y guarda para confirmar.`,
				});
			} catch (error) {
				console.error('Error al cargar pasajero existente:', error);
				onSetAlert({
					show: true,
					severity: 'error',
					message: 'Error al cargar el pasajero existente. Intenta nuevamente.',
				});
			}
		},
		[passengersState, places, mercado, newPassengerType, onSetAlert],
	);

	const handleTogglePassengerExpansion = (keyId: string) => {
		setExpandedPassenger(expandedPassenger === keyId ? null : keyId);
	};

	const handleFetchRelated = useCallback(
		async (passengerId: string) => {
			// Toggle: si ya están abiertos los sugeridos de este pax, cerrar
			if (relatedPaxSuggestions?.forPassengerId === passengerId) {
				setRelatedPaxSuggestions(null);
				return;
			}

			setLoadingRelatedForId(passengerId);
			try {
				const { data } = await getRelatedPaxes(passengerId);

				// Excluir los que ya están cargados en la orden
				const existingIds = new Set(passengersState.map((p) => p.id));
				const filtered = data.filter((p) => !existingIds.has(p.id));

				if (filtered.length === 0) {
					onSetAlert({
						show: true,
						severity: 'info',
						message: 'No se encontraron pasajeros relacionados nuevos',
					});
					setRelatedPaxSuggestions(null);
				} else {
					setRelatedPaxSuggestions({ forPassengerId: passengerId, suggestions: filtered });
				}
			} catch {
				onSetAlert({
					show: true,
					severity: 'error',
					message: 'Error al buscar pasajeros relacionados',
				});
			} finally {
				setLoadingRelatedForId(null);
			}
		},
		[relatedPaxSuggestions, passengersState, onSetAlert],
	);

	useEffect(() => {
		const newState = passengers.map((pax) => ({
			...pax,
			documents: pax.documents.map((doc) => ({
				...doc,
				country: doc.country.code,
				image: cleanBase64Image(doc.image || null),
			})),
		}));
		//@ts-ignore
		setPassengersState(newState);
		// Resetear el flag cuando se actualizan los datos desde la DB
		setHasAddedNewPassengers(false);
	}, [passengers]);

	useEffect(() => {
		const fetchPlaces = async () => {
			const { data: result } = await getNetsuitePlaces();
			setPlaces(result);
		};
		fetchPlaces();
	}, [setPlaces]);

	// Separar pasajeros (personas físicas) de empresas (personas jurídicas)
	const physicalPersons = passengers.filter((p) => p.isPhysicalPerson);
	const companies = passengers.filter((p) => !p.isPhysicalPerson);

	const totalPhysicalPersons = physicalPersons.length;
	const totalCompanies = companies.length;

	const completedPhysicalPersons = physicalPersons.filter((p) => getPassengerCompletionStatus(p) === 'complete').length;
	const completedCompanies = companies.filter((p) => getPassengerCompletionStatus(p) === 'complete').length;

	return (
		<Box sx={{ p: 3 }}>
			{/* Header con métricas */}
			<Box mb={4}>
				<Typography variant="h6" fontWeight={700} gutterBottom>
					Gestión de Pasajeros
				</Typography>

				<Box display="flex" gap={3} mb={3} flexWrap="wrap">
					{/* Contador de pasajeros (personas físicas) */}
					{totalPhysicalPersons > 0 && (
						<Box display="flex" alignItems="center" gap={1}>
							<PersonIcon sx={{ fontSize: 16, color: 'primary.main' }} />
							<Typography variant="h6" fontWeight={600}>
								{totalPhysicalPersons}
							</Typography>
							<Typography variant="body2" color="text.secondary">
								pasajero{totalPhysicalPersons !== 1 ? 's' : ''}
							</Typography>
							<Box display="flex" alignItems="center" gap={0.5}>
								<FiberManualRecordIcon sx={{ color: 'success.main', fontSize: 8 }} />
								<Typography variant="caption" color="text.secondary">
									{completedPhysicalPersons}
								</Typography>
								<FiberManualRecordIcon sx={{ color: 'warning.main', fontSize: 8 }} />
								<Typography variant="caption" color="text.secondary">
									{totalPhysicalPersons - completedPhysicalPersons}
								</Typography>
							</Box>
						</Box>
					)}

					{/* Contador de empresas (personas jurídicas) */}
					{totalCompanies > 0 && (
						<Box display="flex" alignItems="center" gap={1}>
							<BusinessIcon sx={{ fontSize: 16, color: 'secondary.main' }} />
							<Typography variant="h6" fontWeight={600}>
								{totalCompanies}
							</Typography>
							<Typography variant="body2" color="text.secondary">
								empresa{totalCompanies !== 1 ? 's' : ''}
							</Typography>
							<Box display="flex" alignItems="center" gap={0.5}>
								<FiberManualRecordIcon sx={{ color: 'success.main', fontSize: 8 }} />
								<Typography variant="caption" color="text.secondary">
									{completedCompanies}
								</Typography>
								<FiberManualRecordIcon sx={{ color: 'warning.main', fontSize: 8 }} />
								<Typography variant="caption" color="text.secondary">
									{totalCompanies - completedCompanies}
								</Typography>
							</Box>
						</Box>
					)}

					{/* Estado cuando no hay nadie */}
					{totalPhysicalPersons === 0 && totalCompanies === 0 && (
						<Typography variant="body2" color="text.secondary">
							Sin pasajeros ni empresas agregadas
						</Typography>
					)}
				</Box>
			</Box>

			{/* Lista de pasajeros existentes */}
			{passengersState.length > 0 && (
				<Box mb={4}>
					{passengersState.map((passenger) => (
						<React.Fragment key={passenger.keyId}>
							<PassengerCard
								passenger={passenger}
								isExpanded={expandedPassenger === passenger.keyId}
								onToggleExpand={() => handleTogglePassengerExpansion(passenger.keyId)}
								onRemove={handleRemovePax}
								places={places}
								onUpdatePassengers={onUpdatePassengers}
								onSetAlert={onSetAlert}
								onFetchRelated={handleFetchRelated}
								loadingRelated={loadingRelatedForId === passenger.keyId}
							/>
							{relatedPaxSuggestions !== null &&
								relatedPaxSuggestions.forPassengerId === passenger.keyId &&
								relatedPaxSuggestions.suggestions.length > 0 && (
									<SuggestedPaxCards
										suggestions={relatedPaxSuggestions.suggestions}
										onAdd={(id) => {
											handleSelectExistingPassenger(id);
											setRelatedPaxSuggestions(null);
										}}
									/>
								)}
						</React.Fragment>
					))}
				</Box>
			)}

			{/* Estado vacío o botón para agregar */}
			{passengersState.length === 0 && !showNewPassengerCreator && (
				<Alert
					severity="info"
					sx={{ mb: 3 }}
					action={
						<Button color="inherit" size="small" onClick={() => setShowNewPassengerCreator(true)}>
							Agregar Pasajero
						</Button>
					}
				>
					No hay pasajeros en esta orden. Agrega al menos un pasajero para continuar.
				</Alert>
			)}

			{/* Creador de nuevo pasajero */}
			{showNewPassengerCreator && places && (
				<NewPassengerCreator
					onCreatePassenger={handleCreateNewPassenger}
					onSelectExistingPassenger={handleSelectExistingPassenger}
					isPhysicalPerson={newPassengerType}
					onTogglePersonType={() => setNewPassengerType(!newPassengerType)}
					existingPassengers={passengersState.map((p) => ({
						keyId: p.keyId,
						name: p.name,
						lastName: p.lastName,
						birthdate: p.birthdate,
						passengerId: p.keyId, // Usar keyId como passengerId para la comparación
					}))}
					onDuplicateAlert={(message) => {
						onSetAlert({
							show: true,
							severity: 'warning',
							message,
						});
					}}
				/>
			)}

			{/* Botón para agregar más pasajeros */}
			{passengersState.length > 0 && !showNewPassengerCreator && (
				<Button
					variant="outlined"
					startIcon={<PersonAddIcon />}
					onClick={() => setShowNewPassengerCreator(true)}
					size="large"
				>
					Agregar Otro Pasajero
				</Button>
			)}

			{/* Modal de confirmación para cambios no guardados */}
			<Dialog open={unsavedModal.open} onClose={() => handleUnsavedModalResponse('stay')} maxWidth="sm" fullWidth>
				<DialogTitle>
					<Box display="flex" alignItems="center" gap={1}>
						<Typography variant="h6" component="span">
							⚠️ Tienes cambios sin guardar
						</Typography>
					</Box>
				</DialogTitle>
				<DialogContent>
					<DialogContentText>
						Tienes pasajeros con cambios que no han sido guardados. Si continúas, estos cambios se perderán.
					</DialogContentText>
					<Box mt={2}>
						<Typography variant="body2" fontWeight={600} gutterBottom>
							¿Qué quieres hacer?
						</Typography>
						<Typography variant="body2" color="text.secondary">
							• <strong>Quedarme aquí:</strong> Para revisar y guardar los cambios manualmente
							<br />• <strong>Salir sin guardar:</strong> Los cambios se perderán permanentemente
						</Typography>
					</Box>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => handleUnsavedModalResponse('stay')} variant="contained" color="primary">
						Quedarme aquí
					</Button>
					<Button onClick={() => handleUnsavedModalResponse('leave')} variant="outlined" color="error">
						Salir sin guardar
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
};
